import { useCallback, useEffect, useRef } from 'react';

export const useWakeLock = (active: boolean): void => {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  const release = useCallback(async () => {
    if (sentinelRef.current) {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      try {
        await sentinel.release();
      } catch {
        // Ignore release errors
      }
    }
  }, []);

  useEffect(() => {
    if (!active) {
      release();
      return undefined;
    }

    if (!('wakeLock' in navigator)) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (!cancelled) {
          sentinelRef.current = sentinel;
        } else {
          sentinel.release().catch(() => {});
        }
      } catch {
        // Ignore — lock may be denied if page is not visible
      }
    })();

    return () => {
      cancelled = true;
      release();
    };
  }, [active, release]);

  useEffect(() => {
    if (!('wakeLock' in navigator)) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Browser automatically releases the sentinel when hidden — clear our ref
        sentinelRef.current = null;
        return;
      }
      if (document.visibilityState === 'visible' && active && !sentinelRef.current) {
        (async () => {
          try {
            sentinelRef.current = await navigator.wakeLock.request('screen');
          } catch {
            // Ignore — lock may be denied
          }
        })();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [active]);
};
